"use client";

import { type CSSProperties, memo } from "react";
import { motion } from "motion/react";
import { cn } from "@/lib/cn";

/* Declared up front rather than via `motion.create(as)` in render, which would remount — and
   restart the sweep — on every render. Add a tag here when one is needed. */
const TAGS = { p: motion.p, span: motion.span, div: motion.div };

type ShimmerProps = {
  children: string;
  as?: keyof typeof TAGS;
  className?: string;
  /** Seconds per sweep. */
  duration?: number;
  /** Width of the highlight, per character of text. */
  spread?: number;
};

/**
 * Text with a highlight sweeping across it — AI Elements' Shimmer
 * (https://elements.ai-sdk.dev/components/shimmer), recoloured to our tokens: grey text with a
 * white sweep, so it reads on the white cards it sits on.
 */
const ShimmerComponent = ({ children, as = "p", className, duration = 2, spread = 2 }: ShimmerProps) => {
  const MotionComponent = TAGS[as];

  return (
    <MotionComponent
      initial={{ backgroundPosition: "100% center" }}
      animate={{ backgroundPosition: "0% center" }}
      transition={{ repeat: Number.POSITIVE_INFINITY, duration, ease: "linear" }}
      className={cn(
        "relative inline-block bg-[length:250%_100%,auto] bg-clip-text text-transparent",
        "[--bg:linear-gradient(90deg,#0000_calc(50%-var(--spread)),var(--color-neutral-white),#0000_calc(50%+var(--spread)))] [background-repeat:no-repeat,padding-box]",
        className,
      )}
      style={
        {
          "--spread": `${children.length * spread}px`,
          backgroundImage:
            "var(--bg), linear-gradient(var(--color-neutral-grey), var(--color-neutral-grey))",
        } as CSSProperties
      }
    >
      {children}
    </MotionComponent>
  );
};

export const Shimmer = memo(ShimmerComponent);
